import { CalendarIcon } from "@chakra-ui/icons";
import { Badge, Box, Center, Grid, HStack, Td, Text, Tr, VStack } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import React from "react";
import RangeDatePicker from "../../../../../shared/components/DatePicker/RangeDatePicker";
import Select from "../../../../../shared/components/Select";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileTable from "../../../../entityProfileModal/components/EntityProfileTable";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import {
  type CaregiverAssignedTask,
  useCaregiverAssignedTasks,
} from "./CaregiverProfileAssignedTasksSection.hooks";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiverId: CaregiverId;
};

function formatDate(value: unknown) {
  if (formatCaregiverProfileValue(value) === "") {
    return "";
  }

  if (value instanceof Object) {
    return dateFormatter.toDate(value as never);
  }

  if (typeof value === "string") {
    try {
      return dateFormatter.toDate(LocalDate.parse(value));
    } catch {
      return value;
    }
  }

  return String(value);
}

function getTaskField(task: CaregiverAssignedTask, key: string) {
  return (task as Record<string, unknown>)[key];
}

function getTaskStatusLabel(task: CaregiverAssignedTask) {
  const status = getTaskField(task, "status");

  if (typeof status === "object" && status !== null) {
    const statusRecord = status as Record<string, unknown>;
    const label = statusRecord.label;
    const statusValue = statusRecord.status;

    return typeof label === "string" ? label : typeof statusValue === "string" ? statusValue : "";
  }

  const type = getTaskField(task, "type");

  if (type === "FutureTask") {
    return "Future";
  }

  if (type === "DraftTask") {
    return "Unassigned";
  }

  if (type === "Broadcasting") {
    return "Broadcasting";
  }

  if (type !== "RegularTask" && typeof status === "string") {
    return status;
  }

  const canceledAt =
    getTaskField(task, "taskInstanceCanceledAt") ?? getTaskField(task, "canceledAt");

  if (canceledAt !== null && canceledAt !== undefined) {
    return "Canceled";
  }

  const completionDate = getTaskField(task, "completionDate");

  if (completionDate !== null && completionDate !== undefined) {
    return "Completed";
  }

  const scheduleDateTime = getTaskScheduleDate(task);

  if (scheduleDateTime !== null) {
    if (
      getTaskField(task, "hasClockedIn") === true ||
      hasPartialSubmittedDocumentsAfterDueDate(task)
    ) {
      return "Incompleted";
    }

    if (isBeforeNow(scheduleDateTime)) {
      return "Missing";
    }

    return getTaskField(task, "patientConfirmedSchedule") === false
      ? "Scheduled (Pending Patient Confirmation)"
      : "Scheduled";
  }

  return isBeforeNow(getTaskField(task, "dueDate")) ? "Missing" : "Assigned";
}

function hasPartialSubmittedDocumentsAfterDueDate(task: CaregiverAssignedTask) {
  if (isBeforeNow(getTaskField(task, "dueDate")) === false) {
    return false;
  }

  const documents = getTaskField(task, "documents");

  if (Array.isArray(documents) === false || documents.length === 0) {
    return false;
  }

  const submittedDocumentsCount = documents.filter((document) => {
    if (typeof document !== "object" || document === null) {
      return false;
    }

    return (document as Record<string, unknown>).submitted === true;
  }).length;

  return submittedDocumentsCount > 0 && submittedDocumentsCount < documents.length;
}

function getTaskScheduleDate(task: CaregiverAssignedTask) {
  return getTaskField(task, "scheduleDateTime") ?? getTaskField(task, "scheduledDateTime") ?? null;
}

function isBeforeNow(value: unknown) {
  if (value === null || value === undefined) {
    return false;
  }

  const time = new Date(String(value)).getTime();

  return Number.isNaN(time) === false && time < Date.now();
}

function getTaskStatusColorScheme(status: string) {
  if (status === "Completed") {
    return "green";
  }

  if (status === "Canceled" || status === "Missing" || status === "Unassigned") {
    return "red";
  }

  if (status === "Incompleted" || status === "Assigned") {
    return "gray";
  }

  if (status === "Broadcasting" || status === "Scheduled (Pending Patient Confirmation)") {
    return "orange";
  }

  if (status === "Scheduled") {
    return "blue";
  }

  return "gray";
}

function getPatientAddress(task: CaregiverAssignedTask) {
  const patientAddress = getTaskField(task, "patientAddress");

  if (typeof patientAddress === "string") {
    return patientAddress;
  }

  const patient = getTaskField(task, "patient");

  if (typeof patient === "object" && patient !== null) {
    const address = (patient as Record<string, unknown>).address;

    if (typeof address === "object" && address !== null) {
      const text = (address as Record<string, unknown>).text;

      return typeof text === "string" ? text : "";
    }
  }

  return "";
}

function getTaskKey(task: CaregiverAssignedTask) {
  if ("taskInstanceId" in task) {
    return `${task.type}-${task.taskInstanceId}`;
  }

  return `${task.type}-${task.taskId}-${task.startDate}`;
}

export default function CaregiverProfileAssignedTasksSection(props: Props) {
  const [dateRange, setDateRange] = React.useState<[LocalDate | null, LocalDate | null]>([
    null,
    null,
  ]);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [nameFilter, setNameFilter] = React.useState<string[]>([]);
  const tasksQuery = useCaregiverAssignedTasks(props.caregiverId, dateRange[0], dateRange[1]);
  const tasks = React.useMemo(
    () => tasksQuery.data?.caregiverTaskInstances ?? [],
    [tasksQuery.data?.caregiverTaskInstances]
  );
  const statusOptions = React.useMemo(
    () =>
      Array.from(new Set(tasks.map(getTaskStatusLabel).filter((value) => value !== "")))
        .sort((first, second) => first.localeCompare(second))
        .map((status) => ({ label: status, value: status })),
    [tasks]
  );
  const taskNameOptions = React.useMemo(
    () =>
      Array.from(new Set(tasks.map((task) => task.title).filter((value) => value !== "")))
        .sort((first, second) => first.localeCompare(second))
        .map((title) => ({ label: title, value: title })),
    [tasks]
  );
  const filteredTasks = React.useMemo(
    () =>
      tasks.filter((task) => {
        const matchesStatus =
          statusFilter.length === 0 || statusFilter.includes(getTaskStatusLabel(task));
        const matchesName = nameFilter.length === 0 || nameFilter.includes(task.title);

        return matchesStatus && matchesName;
      }),
    [nameFilter, statusFilter, tasks]
  );
  const selectButtonProps = {
    _hover: { bg: "#fff", borderColor: "#adadad" },
    bg: "#fff",
    borderColor: "#ccc",
    borderRadius: "2px",
    color: "#333",
    fontSize: "15px",
    fontWeight: 400,
    h: "38px",
    minW: "220px",
    px: "14px",
  };
  const filters = (
    <Grid alignItems="end" gap={6} templateColumns="repeat(3, minmax(220px, max-content))">
      <Box>
        <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
          Pick Range
        </Text>
        <HStack alignItems="center" spacing={2}>
          <RangeDatePicker
            endDate={dateRange[1]}
            inputProps={{
              borderColor: "#ccc",
              borderRadius: "2px",
              color: "#555",
              fontSize: "15px",
              fontWeight: 600,
              h: "38px",
            }}
            startDate={dateRange[0]}
            onChange={(range) => {
              if (range === null) {
                setDateRange([null, null]);
                return;
              }

              const selectedStartDate = range[0];

              if (selectedStartDate === undefined) {
                return;
              }

              setDateRange([selectedStartDate, range[1] ?? null]);
            }}
          />
          {dateRange[0] !== null ? (
            <EntityModalButton
              isIconOnly
              aria-label="Reset date range"
              iconClassName="fa fa-times"
              kind="white"
              style={{ height: 38, minWidth: 34, width: 34 }}
              title="Reset date range"
              onClick={() => setDateRange([null, null])}
            />
          ) : null}
        </HStack>
      </Box>
      <Box>
        <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
          Task Status
        </Text>
        <Select
          buttonProps={selectButtonProps}
          label="Select"
          multiple={true}
          options={statusOptions}
          selectedLabel={(value) => (value.length === 1 ? value[0] : `${value.length} selected`)}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value ?? [])}
        />
      </Box>
      <Box>
        <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
          Task Name
        </Text>
        <Select
          buttonProps={selectButtonProps}
          label="Select"
          multiple={true}
          options={taskNameOptions}
          selectedLabel={(value) => (value.length === 1 ? value[0] : `${value.length} selected`)}
          value={nameFilter}
          onChange={(value) => setNameFilter(value ?? [])}
        />
      </Box>
    </Grid>
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load assigned tasks."
      filters={filters}
      isError={tasksQuery.isError}
      isLoading={tasksQuery.isLoading}
      loadingLabel="Loading assigned tasks"
      onRetry={() => void tasksQuery.refetch()}
    >
      {filteredTasks.length === 0 ? (
        <Center color={entityProfileModalColors.title} minH="260px" textAlign="center">
          <VStack spacing={5}>
            <Text fontSize="24px" fontWeight={300} px="25%">
              Caregiver does not have any tasks for these dates
            </Text>
            <CalendarIcon boxSize="76px" color={entityProfileModalColors.title} />
          </VStack>
        </Center>
      ) : (
        <EntityProfileTable
          columns={[
            "Task Date",
            "Due Date Range",
            "Task Name",
            "Patient name",
            "Patient Address",
            "Task Status",
          ]}
        >
          {filteredTasks.map((task) => (
            <Tr key={getTaskKey(task)}>
              <Td>
                {getTaskScheduleDate(task) === null ? (
                  <Text color="red.500">Not scheduled</Text>
                ) : (
                  formatDate(getTaskScheduleDate(task))
                )}
              </Td>
              <Td>
                {formatDate(task.startDate)} - {formatDate(task.dueDate)}
              </Td>
              <Td fontWeight={700}>{task.title}</Td>
              <Td>
                <EntityModalButton
                  kind="blue"
                  variant="link"
                  onClick={() => props.actions.navigateToPatient(task.patientId)}
                >
                  {task.patientName}
                </EntityModalButton>
              </Td>
              <Td>{getPatientAddress(task)}</Td>
              <Td>
                <Badge colorScheme={getTaskStatusColorScheme(getTaskStatusLabel(task))}>
                  {getTaskStatusLabel(task)}
                </Badge>
              </Td>
            </Tr>
          ))}
        </EntityProfileTable>
      )}
    </EntityProfileQuerySection>
  );
}
